import Logo from "@/components/Logo";
import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA] flex flex-col">
      <div className="grain" />
      <div className="px-6 lg:px-8 h-20 flex items-center max-w-7xl mx-auto w-full">
        <Link to="/" data-testid="reset-logo" aria-label="MBM home"><Logo className="h-7 w-auto" /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md" data-testid="reset-page">
          <div className="section-label text-[10px] text-[#9fd3ff]">Account recovery</div>
          <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-light tracking-tight">Set a new password.</h1>
          {!token ? (
            <p className="mt-6 text-sm text-red-400 font-light" data-testid="reset-no-token">
              This link is missing its reset token. Please request a new link from the{" "}
              <Link to="/forgot-password" className="text-[#9fd3ff] hover:text-white">forgot password</Link> page.
            </p>
          ) : done ? (
            <div className="mt-8 glass-card p-8" data-testid="reset-done">
              <p className="text-sm text-zinc-300 font-light leading-relaxed">
                Your password has been updated. Redirecting you to sign in…
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="reset-form">
              <div>
                <label className="section-label text-[9px] text-zinc-500">New password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-4 py-3 text-sm outline-none transition-colors"
                  data-testid="reset-password" />
              </div>
              <div>
                <label className="section-label text-[9px] text-zinc-500">Confirm password</label>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                  className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#6ea8ff]/60 rounded-lg px-4 py-3 text-sm outline-none transition-colors"
                  data-testid="reset-confirm" />
              </div>
              {error && <div className="text-sm text-red-400 font-light" data-testid="reset-error">{error}</div>}
              <button type="submit" disabled={busy}
                className="btn-solid w-full py-3.5 text-sm font-medium tracking-wide disabled:opacity-50" data-testid="reset-submit">
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
